import type { ProyectoPlan, PlanModulo } from '../tipos'
import type { MesPyg, TramoPlan, AvanceHistorial, ResumenPeriodoPyg, FichaProyectoPyg, RangoPyg } from './tipos'

const MESES_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

export function etiquetaMes(clave: string): string {
  const [a, m] = clave.split('-')
  const idx = Number(m) - 1
  return `${MESES_ES[idx] || m} ${a.slice(2)}`
}

export function sumaMesClave(clave: string, n: number): string {
  const [a, m] = clave.split('-').map(Number)
  const d = new Date(a, m - 1 + n, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** Cuántos días de un tramo caen dentro del mes analizado. */
export function diasEnElMes(inicioStr: string, finStr: string, claveMes: string): number {
  const [a, m] = claveMes.split('-').map(Number)
  const inicioMes = new Date(a, m - 1, 1).getTime()
  const finMes = new Date(a, m, 0, 23, 59, 59, 999).getTime()

  const i = new Date(inicioStr + 'T00:00:00').getTime()
  const f = new Date(finStr + 'T23:59:59').getTime()

  const a0 = Math.max(inicioMes, i)
  const a1 = Math.min(finMes, f)
  if (a1 <= a0) return 0

  return Math.round((a1 - a0) / 86_400_000) + 1
}

/** Duración total en días entre dos fechas (inclusivo). */
export function diasTotales(inicioStr: string, finStr: string): number {
  const i = new Date(inicioStr + 'T00:00:00').getTime()
  const f = new Date(finStr + 'T23:59:59').getTime()
  if (f <= i) return 1
  return Math.max(1, Math.round((f - i) / 86_400_000) + 1)
}

/** TARIFA BASE ESTIMADA DE HORA TÉCNICA (COP) si no se especifica otra. */
export const TARIFA_HORA_TECNICO_COP = 28_000

/**
 * Convierte las etapas y módulos de los proyectos en tramos de planeación temporal.
 */
export function extraerTramosPlan(proyectos: ProyectoPlan[]): TramoPlan[] {
  const tramos: TramoPlan[] = []

  for (const proy of proyectos) {
    for (const etapa of proy.etapas) {
      const precioEtapa = etapa.modulos.reduce((sum, m) => sum + (Number(m.valor_total) || 0), 0)
      
      // Costo directo estimado: mano de obra (horas * tarifa) + costo de materiales
      const costoEtapa = etapa.modulos.reduce((sum, m) => {
        const horas = Number(m.horas) || 0
        const costoHoras = horas * TARIFA_HORA_TECNICO_COP
        // Si no hay horas, se estima costo de producción al 40% del valor del módulo
        const costoMano = costoHoras > 0 ? costoHoras : (Number(m.valor_total) || 0) * 0.35
        const costoMats = (Number(m.valor_total) || 0) * 0.15
        return sum + costoMano + costoMats
      }, 0)

      const fInicio = etapa.fecha_inicio || proy.fecha_inicio
      const fFin = etapa.fecha_fin || proy.fecha_fin

      if (fInicio && fFin) {
        tramos.push({
          etapaId: etapa.id,
          proyectoId: proy.id,
          nombre: `${proy.codigo}: ${etapa.nombre}`,
          inicio: fInicio.slice(0, 10),
          fin: fFin.slice(0, 10),
          semanas: Number(etapa.semanas) || 1,
          precio: precioEtapa,
          costo: costoEtapa,
        })
      }
    }
  }

  return tramos
}

/**
 * Calcula el Estado de Resultados Real (P&G Devengado) mes a mes idéntico al de Arkano Hub.
 */
export function calcularDevengadoProyectos(opts: {
  hoy: Date
  proyectos: ProyectoPlan[]
  historialAvances?: AvanceHistorial[]
  proyectoIdSeleccionado?: string | null
  actasCorte?: any[]
  costoIndirectoTotalMes?: number
}): MesPyg[] {
  const {
    hoy,
    proyectos,
    historialAvances = [],
    proyectoIdSeleccionado = null,
    actasCorte = [],
    costoIndirectoTotalMes = 15_000_000,
  } = opts

  // Filtrar proyectos si hay uno específico seleccionado
  const proyectosFiltrados = proyectoIdSeleccionado
    ? proyectos.filter((p) => p.id === proyectoIdSeleccionado)
    : proyectos

  if (proyectosFiltrados.length === 0) return []

  const mesHoy = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`
  const tramos = extraerTramosPlan(proyectosFiltrados)

  // 1. Recopilar todas las claves de meses relevantes (desde el inicio más temprano hasta el fin más lejano)
  const fechasFechas: string[] = [mesHoy]

  for (const t of tramos) {
    if (t.inicio) fechasFechas.push(t.inicio.slice(0, 7))
    if (t.fin) fechasFechas.push(t.fin.slice(0, 7))
  }

  for (const p of proyectosFiltrados) {
    if (p.fecha_inicio) fechasFechas.push(p.fecha_inicio.slice(0, 7))
    if (p.fecha_fin) fechasFechas.push(p.fecha_fin.slice(0, 7))
  }

  for (const a of historialAvances) {
    if (a.mes) fechasFechas.push(a.mes)
  }

  const fechasValidas = fechasFechas.filter(Boolean).sort()
  if (fechasValidas.length === 0) return []

  const claveMin = fechasValidas[0]
  const claveMax = fechasValidas[fechasValidas.length - 1]

  // Generar secuencia continua de meses
  const mesesClaves: string[] = []
  for (let c = claveMin; c <= claveMax; c = sumaMesClave(c, 1)) {
    mesesClaves.push(c)
  }

  // Mapa rápido de módulos para asignación de valor
  const mapaModulos = new Map<string, PlanModulo>()
  for (const p of proyectosFiltrados) {
    for (const e of p.etapas) {
      for (const m of e.modulos) {
        mapaModulos.set(m.id, m)
      }
    }
  }

  // 2. Procesar cada mes con acumuladores (Tarea 2.9 y 2.11)
  let cobradoAcumulado = 0
  let costoAcumulado = 0
  let utilidadAcumulada = 0

  const presupuestoTotalContrato = proyectosFiltrados.reduce(
    (sum, p) => sum + Number(p.tope_contrato || p.presupuesto || 0),
    0
  )

  return mesesClaves.map((clave) => {
    const esActual = clave === mesHoy
    const futuro = clave > mesHoy

    // A. PLAN TEÓRICO (Debía producirse en este mes según cronograma)
    let planeado = 0
    let costoPlaneado = 0

    for (const tramo of tramos) {
      const diasMes = diasEnElMes(tramo.inicio, tramo.fin, clave)
      if (diasMes > 0) {
        const totalDias = diasTotales(tramo.inicio, tramo.fin)
        const fraccion = Math.min(1, Math.max(0, diasMes / totalDias))
        planeado += Math.round(tramo.precio * fraccion)
        costoPlaneado += Math.round(tramo.costo * fraccion)
      }
    }

    // B. PRODUCCIÓN REAL DEVENGADA
    let produccion = 0
    let costoManoObra = 0
    let costoMateriales = 0

    // Si hay registros históricos para este mes, usarlos directamente
    const avancesDelMes = historialAvances.filter((h) => h.mes === clave)

    if (avancesDelMes.length > 0) {
      for (const h of avancesDelMes) {
        const mod = mapaModulos.get(h.modulo_id)
        if (mod) {
          const valor = Number(mod.valor_total) || 0
          const deltaPct = (Number(h.delta) || 0) / 100
          const prodDevengada = valor * deltaPct
          produccion += prodDevengada

          // Costos directos proporcionales
          const horasReales = (Number(mod.horas_reales) || Number(mod.horas) || 0) * deltaPct
          costoManoObra += horasReales * TARIFA_HORA_TECNICO_COP
          costoMateriales += prodDevengada * 0.15
        }
      }
    } else if (!futuro) {
      // Fallback analítico: prorratear el avance actual de los módulos que caen en este mes
      for (const proy of proyectosFiltrados) {
        for (const etapa of proy.etapas) {
          const diasMes = diasEnElMes(etapa.fecha_inicio || proy.fecha_inicio, etapa.fecha_fin || proy.fecha_fin, clave)
          if (diasMes > 0) {
            const totalDias = diasTotales(etapa.fecha_inicio || proy.fecha_inicio, etapa.fecha_fin || proy.fecha_fin)
            const fraccionMes = diasMes / totalDias

            for (const m of etapa.modulos) {
              const valorMod = Number(m.valor_total) || 0
              const avancePct = (Number(m.avance) || 0) / 100
              const producidoModulo = valorMod * avancePct * fraccionMes
              produccion += producidoModulo

              const horasMano = (Number(m.horas_reales) || Number(m.horas) || 0) * avancePct * fraccionMes
              costoManoObra += horasMano * TARIFA_HORA_TECNICO_COP
              costoMateriales += producidoModulo * 0.15
            }
          }
        }
      }
    }

    produccion = Math.round(produccion)
    costoManoObra = Math.round(costoManoObra)
    costoMateriales = Math.round(costoMateriales)
    const costoDirecto = costoManoObra + costoMateriales
    const margen = produccion - costoDirecto
    const margenPct = produccion > 0 ? Math.round((margen / produccion) * 100) : 0
    const utilidad = margen

    const utilidadPlaneada = planeado - costoPlaneado
    const cumplimiento = planeado > 0 ? Math.round((produccion / planeado) * 100) : 0

    // Tarea 2.9: Dinero efectivamente cobrado en ese mes según actas recaudadas
    const actasMes = actasCorte.filter(
      (a: any) =>
        a.estado === 'cobrada' &&
        a.fecha_cobro &&
        a.fecha_cobro.startsWith(clave) &&
        (!proyectoIdSeleccionado || a.proyecto_id === proyectoIdSeleccionado)
    )
    const cobradoMes = actasMes.reduce((s: number, a: any) => s + Number(a.valor_total || 0), 0)

    // Utilidad del periodo: lo cobrado de ESE mes menos los costos de ESE mes
    const ingresoEfectivoMes = cobradoMes > 0 ? cobradoMes : (futuro ? 0 : produccion)
    const utilidadPeriodo = ingresoEfectivoMes - costoDirecto

    // Margen sobre lo cobrado y sobre lo contratado
    const margenSobreCobradoPct =
      cobradoMes > 0 ? Math.round(((cobradoMes - costoDirecto) / cobradoMes) * 100) : margenPct
    const margenSobreContratadoPct =
      presupuestoTotalContrato > 0
        ? Math.round((utilidadPeriodo / presupuestoTotalContrato) * 100)
        : 0

    // Acumulados desde que arrancó la obra
    if (!futuro) {
      cobradoAcumulado += cobradoMes
      costoAcumulado += costoDirecto
      utilidadAcumulada += utilidadPeriodo
    }

    // Tarea 2.11: Prorrateo de costos indirectos entre obras en base a lo ejecutado
    // (Prorrata de lo ejecutado en el periodo sobre el total ejecutado)
    const fraccionProrrata =
      proyectos.length > 0 ? Math.min(1, 1 / Math.max(1, proyectos.length)) : 1
    const indirectosProrrateados = futuro ? 0 : Math.round(costoIndirectoTotalMes * fraccionProrrata)
    const margenDirecto = margen
    const margenNeto = margenDirecto - indirectosProrrateados
    const margenNetoPct =
      produccion > 0 ? Math.round((margenNeto / produccion) * 100) : 0

    return {
      clave,
      etiqueta: etiquetaMes(clave),
      esActual,
      futuro,
      produccion,
      costoManoObra,
      costoMateriales,
      costoDirecto,
      margen,
      margenPct,
      planeado,
      costoPlaneado,
      utilidadPlaneada,
      utilidad,
      cumplimiento,
      cobradoMes,
      utilidadPeriodo,
      margenSobreCobradoPct,
      margenSobreContratadoPct,
      cobradoAcumulado,
      costoAcumulado,
      utilidadAcumulada,
      indirectosProrrateados,
      margenDirecto,
      margenNeto,
      margenNetoPct,
    }
  })
}

/**
 * Agrega el resumen del periodo según el rango seleccionado (mes, trimestre, año o todo).
 */
export function resumirPeriodoPyg(
  meses: MesPyg[],
  mesActualClave: string,
  rango: RangoPyg
): ResumenPeriodoPyg | null {
  if (meses.length === 0) return null

  const cuantos = rango === 'mes' ? 1 : rango === 'trim' ? 3 : rango === 'ano' ? 12 : Infinity
  const hastaI = meses.findIndex((m) => m.clave === mesActualClave)
  const baseHasta = hastaI >= 0 ? hastaI : meses.length - 1

  const desdeI = Math.max(0, cuantos === Infinity ? 0 : baseHasta - cuantos + 1)
  const seleccionados = meses.slice(desdeI, baseHasta + 1).filter((m) => !m.futuro)

  if (seleccionados.length === 0) return null

  const planeado = seleccionados.reduce((acc, m) => acc + m.planeado, 0)
  const produccion = seleccionados.reduce((acc, m) => acc + m.produccion, 0)
  const costoManoObra = seleccionados.reduce((acc, m) => acc + m.costoManoObra, 0)
  const costoMateriales = seleccionados.reduce((acc, m) => acc + m.costoMateriales, 0)
  const costoDirecto = seleccionados.reduce((acc, m) => acc + m.costoDirecto, 0)
  const margen = produccion - costoDirecto
  const margenPct = produccion > 0 ? Math.round((margen / produccion) * 100) : 0
  const utilidadPlaneada = seleccionados.reduce((acc, m) => acc + m.utilidadPlaneada, 0)
  const utilidadReal = margen
  const cumplimientoPct = planeado > 0 ? Math.round((produccion / planeado) * 100) : 0
  const desviacionUtilidad = utilidadReal - utilidadPlaneada

  return {
    planeado,
    produccion,
    costoDirecto,
    costoManoObra,
    costoMateriales,
    margen,
    margenPct,
    utilidadPlaneada,
    utilidadReal,
    cumplimientoPct,
    desviacionUtilidad,
  }
}

/**
 * Genera la ficha individual de rentabilidad de cada proyecto (Cruce por Proyecto).
 */
export function generarFichasProyectosPyg(proyectos: ProyectoPlan[], hoy: Date): FichaProyectoPyg[] {
  const hoyMs = hoy.getTime()

  return proyectos.map((p) => {
    let sumaPresupuesto = 0
    let producidoTotal = 0
    let costoTotal = 0

    for (const etapa of p.etapas) {
      for (const m of etapa.modulos) {
        const val = Number(m.valor_total) || 0
        const av = (Number(m.avance) || 0) / 100
        sumaPresupuesto += val
        producidoTotal += val * av

        const horas = (Number(m.horas_reales) || Number(m.horas) || 0) * av
        const mano = horas * TARIFA_HORA_TECNICO_COP
        const mats = val * av * 0.15
        costoTotal += mano + mats
      }
    }

    const presupuesto = p.presupuesto > 0 ? p.presupuesto : sumaPresupuesto
    producidoTotal = Math.round(producidoTotal)
    costoTotal = Math.round(costoTotal)
    const margenTotal = producidoTotal - costoTotal
    const margenPct = producidoTotal > 0 ? Math.round((margenTotal / producidoTotal) * 100) : 0
    const avanceGlobalPct = presupuesto > 0 ? Math.round((producidoTotal / presupuesto) * 100) : 0

    // Cálculo de tiempo transcurrido del proyecto
    const fIni = new Date(p.fecha_inicio + 'T00:00:00').getTime()
    const fFin = new Date(p.fecha_fin + 'T23:59:59').getTime()
    const transcurrido = Math.max(0, Math.min(1, fFin > fIni ? (hoyMs - fIni) / (fFin - fIni) : 0))
    const planeadoAHoy = Math.round(presupuesto * transcurrido)
    const cumplimientoAHoyPct = planeadoAHoy > 0 ? Math.round((producidoTotal / planeadoAHoy) * 100) : 0

    return {
      id: p.id,
      codigo: p.codigo,
      nombre: p.nombre,
      cliente: p.cliente ?? null,
      presupuesto,
      producidoTotal,
      costoTotal,
      margenTotal,
      margenPct,
      avanceGlobalPct,
      planeadoAHoy,
      cumplimientoAHoyPct,
      estado: p.estado,
      fecha_inicio: p.fecha_inicio,
      fecha_fin: p.fecha_fin,
    }
  })
}
